import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Apple, Play, Bell, Check, Clock, WifiOff, ShieldCheck, Monitor, Smartphone } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { notifyMobileLaunch } from "@/lib/api";

// Carrousel mobile : écrans de l'app mobile uniquement (la vitrine tous-modules est AppShowcaseSection).
const SHOWCASE = [
  { type: "phone", src: "/mobile/dashboard.png", label: "App mobile — Tableau de bord", kind: "mobile" },
  { type: "phone", src: "/mobile/modules.png", label: "App mobile — Modules", kind: "mobile" },
  { type: "phone", src: "/mobile/billing.png", label: "App mobile — Facturation", kind: "mobile" },
];

function ShowcaseCarousel({ soon }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const n = SHOWCASE.length;

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => setI((v) => (v + 1) % n), 4200);
    return () => clearInterval(timer.current);
  }, [paused, n]);

  const item = SHOWCASE[i];

  return (
    <div
      className="relative min-w-0 h-[440px] sm:h-[480px] flex items-center justify-center"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      data-testid="mobile-app-visual"
    >
      <div className="pointer-events-none absolute inset-0 rounded-[40px] bg-gradient-to-br from-[#EAB308]/12 via-transparent to-[#dc143c]/12 blur-3xl" />

      {/* Étiquette module */}
      <div className="absolute z-30 top-0 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="showcase-label">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/70 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white backdrop-blur">
          {item.kind === "mobile" ? <Smartphone className="w-3.5 h-3.5 text-[#EAB308]" /> : <Monitor className="w-3.5 h-3.5 text-[#EAB308]" />}
          {item.label}
        </span>
        {item.kind === "mobile" && (
          <span className="inline-flex items-center rounded-full bg-[#dc143c] px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-white">{soon}</span>
        )}
      </div>

      {/* Slide animé */}
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          className="absolute inset-x-0 top-14 bottom-10 flex items-center justify-center"
          initial={{ opacity: 0, y: 22, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -22, scale: 0.96 }}
          transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        >
          {item.type === "browser" ? (
            <div className="pg-browser pg-browser--front w-[94%]" style={{ transform: "rotate(-1.5deg)" }}>
              <div className="pg-browser__bar">
                <span className="pg-browser__dot" style={{ background: "#dc143c" }} />
                <span className="pg-browser__dot" style={{ background: "#EAB308" }} />
                <span className="pg-browser__dot" style={{ background: "#22c55e" }} />
                <span className="pg-browser__url">{item.url}</span>
              </div>
              <img src={item.src} alt={item.label} loading="lazy" className="pg-browser__screen" />
            </div>
          ) : (
            <div className="pg-phone pg-phone--front w-[196px] sm:w-[210px]" style={{ transform: "rotate(2deg)" }}>
              <div className="pg-phone__notch" />
              <img src={item.src} alt={item.label} loading="lazy" className="pg-phone__screen" />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Points de navigation */}
      <div className="absolute z-30 bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="showcase-dots">
        {SHOWCASE.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setI(idx)}
            aria-label={`Voir ${SHOWCASE[idx].label}`}
            data-testid={`showcase-dot-${idx}`}
            className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-[#EAB308]" : "w-2 bg-white/25 hover:bg-white/50"}`}
          />
        ))}
      </div>
    </div>
  );
}


const T = {
  fr: {
    eyebrow: "Aperçu réel",
    titleA: "Vos modules PROGÈRE,",
    titleB: "déjà en action",
    soon: "Arrive bientôt",
    desc: "Des interfaces réelles, pensées pour la construction au Québec. Et bientôt, tout l'écosystème PROGÈRE dans votre poche sur iPhone et Android.",
    feat: [
      { icon: Clock, t: "Poinçon & feuilles de temps en un geste" },
      { icon: WifiOff, t: "Mode hors ligne sur le chantier" },
      { icon: ShieldCheck, t: "Sécurisé — hébergé au Canada, Loi 25" },
    ],
    formLabel: "Prévenez-moi au lancement de l'app mobile",
    placeholder: "votre@courriel.com",
    submit: "M'avertir",
    done: "Merci ! Vous serez averti dès le lancement.",
    invalid: "Entrez une adresse courriel valide.",
    error: "Impossible d'enregistrer votre courriel. Réessayez.",
    appstore: "Bientôt sur l'App Store",
    play: "Bientôt sur Google Play",
  },
  en: {
    eyebrow: "Live preview",
    titleA: "Your PROGÈRE modules,",
    titleB: "already in action",
    soon: "Coming soon",
    desc: "Real interfaces, built for construction in Québec. And soon, the whole PROGÈRE ecosystem in your pocket on iPhone and Android.",
    feat: [
      { icon: Clock, t: "Clock-in & timesheets in one tap" },
      { icon: WifiOff, t: "Offline mode on the job site" },
      { icon: ShieldCheck, t: "Secure — hosted in Canada, Law 25" },
    ],
    formLabel: "Notify me when the mobile app launches",
    placeholder: "you@email.com",
    submit: "Notify me",
    done: "Thanks! We'll let you know at launch.",
    invalid: "Enter a valid email address.",
    error: "Couldn't save your email. Please try again.",
    appstore: "Soon on the App Store",
    play: "Soon on Google Play",
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export function MobileAppSection() {
  const { lang } = useI18n();
  const c = T[lang === "en" ? "en" : "fr"];
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error(c.invalid); return; }
    setSubmitting(true);
    try {
      const res = await notifyMobileLaunch(v, lang, "landing");
      setDone(true);
      toast.success(res?.message || c.done);
    } catch {
      toast.error(c.error);
    } finally { setSubmitting(false); }
  };

  return (
    <section id="mobile" className="relative py-24 lg:py-32 overflow-hidden" data-testid="mobile-app-section">
      <div className="pointer-events-none absolute top-1/2 right-0 w-[600px] h-[600px] pg-radial-gold opacity-30" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        {/* Texte + form */}
        <div className="min-w-0">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={fadeUp}>
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#dc143c]">{c.eyebrow}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EAB308]/40 bg-[#EAB308]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#EAB308]" data-testid="mobile-soon-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-[#EAB308] animate-pulse" /> {c.soon}
              </span>
            </div>
            <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
              {c.titleA} <span className="text-[#EAB308]">{c.titleB}</span>
            </h2>
            <p className="mt-4 text-white/60 text-lg max-w-xl">{c.desc}</p>
          </motion.div>

          <motion.ul className="mt-8 space-y-3" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
            {c.feat.map((f, i) => (
              <motion.li key={i} custom={i} variants={fadeUp} className="flex items-center gap-3 text-white/75">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-[#EAB308]">
                  <f.icon className="w-4 h-4" />
                </span>
                <span className="text-[15px]">{f.t}</span>
              </motion.li>
            ))}
          </motion.ul>

          {/* Formulaire de capture */}
          <motion.div className="mt-8" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }}>
            {done ? (
              <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-400" data-testid="mobile-landing-done">
                <Check className="w-4 h-4" /> {c.done}
              </div>
            ) : (
              <form onSubmit={submit} className="max-w-md" data-testid="mobile-landing-form">
                <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#EAB308] mb-2">
                  <Bell className="w-3.5 h-3.5" /> {c.formLabel}
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={c.placeholder}
                    autoComplete="email"
                    data-testid="mobile-landing-email"
                    className="flex-1 min-w-0 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#EAB308]/60"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="mobile-landing-submit"
                    className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-[#EAB308] px-6 py-3 text-sm font-bold text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {submitting ? "…" : c.submit}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2.5 rounded-xl border border-white/12 bg-black/50 px-4 py-2.5 text-white/70">
                <Apple className="w-6 h-6" /><span className="flex flex-col leading-tight"><small className="text-[9px] uppercase tracking-wide text-white/45">{c.appstore.split(" ").slice(0,-2).join(" ")}</small><strong className="text-sm text-white">App Store</strong></span>
              </span>
              <span className="inline-flex items-center gap-2.5 rounded-xl border border-white/12 bg-black/50 px-4 py-2.5 text-white/70">
                <Play className="w-5 h-5" /><span className="flex flex-col leading-tight"><small className="text-[9px] uppercase tracking-wide text-white/45">Google</small><strong className="text-sm text-white">Google Play</strong></span>
              </span>
            </div>
          </motion.div>
        </div>

        {/* Vitrine carrousel — modules web + app mobile */}
        <ShowcaseCarousel soon={c.soon} />
      </div>
    </section>
  );
}
